import { useAsyncState, whenever } from '@vueuse/core'
import { defineStore } from 'pinia'
import type { LinkedDashboard, LinkedDashboardGroup, LinkedDashboardsAndGroupsUnion } from '~/types/Dashboards'

export const useLinkedDashboardsStore = defineStore('LinkedDashboards', () => {
  const securityStore = useSecurityStore()
  const { isLoggedIn } = storeToRefs(securityStore)

  const request = ref(Promise.resolve<LinkedDashboardsAndGroupsUnion[]>([]))
  const controller = useAsyncState(() => findLinkedDashboards().then(r => r.data), [], { immediate: false, shallow: true })
  const isLoading = controller.isLoading
  const { state: linkedDashboardsAndGroups, update: updateLinkedDashboards } = useStateMutation(controller.state)

  const linkedDashboards = computed(() => {
    const results: LinkedDashboard[] = []
    linkedDashboardsAndGroups.value.forEach((e) => {
      if (e.type === 'LinkedDashboard') {
        results.push(e)
      } else {
        results.push(...e.dashboards)
      }
    })
    return results
  })

  function load() {
    if (!isLoading.value) {
      request.value = controller.execute()
    }
  }
  function unload() {
    request.value = Promise.resolve([])
    controller.state.value = []
  }
  function addDashboardLink(dashboard: LinkedDashboard, group?: LinkedDashboardGroup) {
    updateLinkedDashboards((linkedDashboards) => {
      const foundGroup = linkedDashboards.find(d => d.id === group?.id)
      if (foundGroup && foundGroup.type === 'LinkedDashboardGroupData') {
        foundGroup.dashboards.push(dashboard)
      } else if (group) {
        linkedDashboards.push(group)
      } else {
        linkedDashboards.push(dashboard)
      }
    })
  }
  function removeDashboardLink(token: string) {
    return unlinkDashboard(token).then(() => {
      updateLinkedDashboards((linkedDashboards) => {
      // Remove a root-level linked dashboard
        for (let i = 0; i < linkedDashboards.length; i++) {
          const entry = linkedDashboards[i]

          if (entry.type === 'LinkedDashboard' && entry.token.id === token) {
            linkedDashboards.splice(i, 1)
            return
          }
        }

        // Otherwise, remove it from a group
        for (let i = 0; i < linkedDashboards.length; i++) {
          const entry = linkedDashboards[i]

          if (entry.type !== 'LinkedDashboardGroupData') {
            continue
          }

          const group = entry

          for (let j = 0; j < group.dashboards.length; j++) {
            const dashboard = group.dashboards[j]

            if (dashboard.type === 'LinkedDashboard' && dashboard.token.id === token) {
              group.dashboards.splice(j, 1)

              // If the group becomes empty, remove the group itself
              if (group.dashboards.length === 0) {
                linkedDashboards.splice(i, 1)
              }

              return
            }
          }
        }
      })
    })
  }

  whenever(isLoggedIn, load, { immediate: true })
  whenever(() => !isLoggedIn.value, unload)
  return {
    isLoading,
    request,
    linkedDashboardsAndGroups,
    addDashboardLink,
    removeDashboardLink,
    linkedDashboards,
  }
})
